import { CANNON_SLOTS } from "../../../shared/BoatTree";
import { BOAT } from "../../../shared/Config";
import { Loadout, sanitiseLoadout } from "../../../shared/Fitout";
import { packColor } from "../../../shared/Settings";

/**
 * Everything Iron Sails remembers about a player between sessions, and the one interface anything
 * else is allowed to reach it through.
 *
 * **Built for the account that is coming, not for the browser it sits in today.** Server-owned
 * player accounts are planned; `localStorage` is the stand-in until they land
 * ([design/Shop.md](../../../design/Shop.md)). So this file is the *shape* of the thing and
 * [LocalStore.ts](LocalStore.ts) is the only implementation of it, and three rules follow that
 * cost nothing now and would cost a rewrite later:
 *
 * - **Every operation is async.** A synchronous read that works today is a synchronous read that
 *   has to be unpicked the day it becomes a request.
 * - **Nothing outside the store sees the blob.** Callers ask for a boat or a balance, never for
 *   the object it is stored in, so the schema can move to a server's without touching the shop.
 * - **Every write is one named operation**, not a read-modify-write spread across the UI. Each
 *   one below becomes a single request when there is somewhere to send it, and `spend` in
 *   particular is a transaction already: it debits and records ownership together or does
 *   neither.
 *
 * The schema of record is [design/AccountData.md](../../../design/AccountData.md), which is
 * sync-critical — a field renamed here is renamed there in the same change, or a returning
 * player's save file quietly stops loading.
 */

/** The `localStorage` key the whole blob lives under. One key, one version. */
export const STORAGE_KEY = "ironsails.player";

/**
 * Bumped on any **breaking** shape change. Additive fields with sensible defaults do not need it.
 *
 * `2` folded the old top-level `cosmetics` block into `locker.equipped` and added `wallet` and
 * `locker` — the rename is what earned the bump, since the two additions on their own would have
 * been free.
 *
 * `3` gave a captain colours that are actually theirs. `hullColour` and `sailColour` were indices
 * into palettes that were never built, and they are **dropped rather than converted**: an index
 * into nothing cannot honestly become a colour, and inventing one would be a made-up boat rather
 * than a restored one. A returning captain keeps their hull, their guns and their wallet and
 * arrives in the catalogue's default colours, which is what they were sailing in anyway. See
 * design/AccountData.md.
 */
export const SCHEMA_VERSION = 3;

/** How long a display name may be. Longer is truncated rather than refused. */
export const NAME_MAX_LENGTH = 16;

/** What a captain owns and what they have equipped. See design/Shop.md. */
export interface Locker {
    /** Owned preset indices into `BOAT.presets`. Index 0 is free and is always in here. */
    presets: number[];
    /** Owned cannon type indices into `BOAT.cannons`. Index 0 is free and is always in here. */
    cannons: number[];
    /** What goes to sea. Exactly the shape the join options carry, plus the cosmetics. */
    equipped: EquippedBoat;
}

/** One boat as a captain has it rigged: which hull, which two guns, and what it looks like. */
export interface EquippedBoat {
    preset: number;
    /** Bow, then stern. Exactly `CANNON_SLOTS` long. */
    cannons: number[];
    /**
     * An index into a list of hull skins that does not exist yet. Kept because it costs a number
     * and phase 9 of the overhaul may still fill it in; nothing reads it.
     */
    hullSkin: number;
    /**
     * The captain's colours, as **`"#rrggbb"` strings** — the same spelling `config/boat.json` and
     * `<input type="color">` use, and readable in devtools, which is where a save file is actually
     * looked at. They are packed to the `uint32` the wire wants by `loadoutOf` below, at the one
     * edge that needs the conversion.
     *
     * **Stored as literals, not as palette indices.** This file used to say the opposite, and the
     * reason it gave — that a stored literal survives a palette being re-ordered under it and
     * becomes a colour the game no longer offers — depended on there being a palette. With free
     * colour there is nothing to re-order and the justification dissolves with it. The hull's wood
     * *is* still picked off a list, and is still stored as the colour rather than the position,
     * so that re-ordering the woods in the dashboard cannot repaint every saved boat.
     */
    sailTint: string;
    hullTint: string;
    emblemTint: string;
    /** Index into `BOAT.cosmetics.emblems`. 0 is no device. */
    emblem: number;
}

/** Local bragging rights. Displayed to the player and never uploaded as fact. */
export interface PlayerRecords {
    bestScore: number;
    /** Best leaderboard position ever held, 1 = top. Zero means "never ranked". */
    bestRank: number;
    longestLifeMs: number;
    totalKills: number;
    totalSinks: number;
    gamesPlayed: number;
}

/** The whole blob. See design/AccountData.md, which is the schema of record. */
export interface StoredPlayerData {
    schemaVersion: number;
    /** UUID v4, generated once. A label, not an authenticator — anyone can craft one. */
    playerId: string;
    /** Last name entered, prefilled on return. Trimmed, `NAME_MAX_LENGTH` at most. */
    displayName: string;
    lastPlayedAt: number;
    settings: {
        musicVolume: number;
        sfxVolume: number;
        muted: boolean;
        quality: "low" | "high";
    };
    wallet: {
        /** Whole doubloons, never negative. Earned by `Ship.purse`, spent in the shop. */
        doubloons: number;
    };
    locker: Locker;
    records: PlayerRecords;
    flags: {
        hasSeenControls: boolean;
    };
}

/** What a captain is buying: which catalogue, which entry, and what it costs. */
export interface Purchase {
    kind: "preset" | "cannon";
    index: number;
    cost: number;
}

/**
 * The store. Every method is async because the next implementation of this is a fetch, and every
 * one of them is a single named operation because the one after that is a route.
 */
export interface PlayerStore {
    /**
     * Everything, with anything unusable replaced by a default. **Never rejects**: storage can be
     * absent, truncated, hand-edited, or from a version this build has never heard of, and none of
     * those is an error state the player should ever be told about.
     */
    load(): Promise<StoredPlayerData>;

    /** The name a captain types. Trimmed and truncated; an empty one leaves the stored one alone. */
    setName(name: string): Promise<void>;

    /**
     * Adds a life's earnings to the wallet, once. The caller passes the last `Ship.purse` it saw,
     * which on the lost-connection route is the last patch before the socket died — the honest
     * number, and the reason the purse is state rather than a farewell message.
     */
    bank(doubloons: number): Promise<void>;

    /** Puts a boat and two guns on the water. Clamped into the catalogue on the way in. */
    equip(boat: EquippedBoat): Promise<void>;

    /**
     * Buys one thing: debits the wallet **and** records ownership, together or not at all. Resolves
     * `false` when the wallet cannot cover it, which is an ordinary answer rather than an error.
     *
     * One operation rather than a read-modify-write in the shop, because on a server it is one
     * request and a client that had already spent the money before the server agreed would be a
     * client that can lose a purchase to a dropped socket.
     */
    spend(purchase: Purchase): Promise<boolean>;
}

/**
 * The loadout a stored boat resolves to, ready for the join options.
 *
 * **Clamped on the way out, every time.** `config/boat.json` is checked in and can shrink under a
 * save file: a stored preset 7 in a catalogue with three of them is not an error, it is a boat
 * that no longer exists, and the answer is the free one. `sanitiseLoadout` is the same function
 * the arena runs on what a client sends, so a save file and a hostile socket degrade identically.
 */
export function loadoutOf(boat: EquippedBoat): Loadout {
    return sanitiseLoadout({
        preset: boat.preset,
        cannons: boat.cannons,
        // Packed here, at the one edge that needs it: the locker stores colours as `"#rrggbb"` so
        // a save file stays readable, and everything past this point wants the `uint32` Pixi's
        // `tint` and the wire both speak.
        sailTint: packColor(boat.sailTint),
        hullTint: packColor(boat.hullTint),
        emblem: boat.emblem,
        emblemTint: packColor(boat.emblemTint)
    });
}

/** A fresh player: the free boat, two free guns, an empty wallet and nothing owned but those. */
export function freshPlayer(): StoredPlayerData {
    return {
        schemaVersion: SCHEMA_VERSION,
        playerId: newPlayerId(),
        displayName: "",
        lastPlayedAt: Date.now(),
        settings: { musicVolume: 0.7, sfxVolume: 0.8, muted: false, quality: "high" },
        wallet: { doubloons: 0 },
        locker: {
            presets: [0],
            cannons: [0],
            equipped: { preset: 0, cannons: new Array<number>(CANNON_SLOTS).fill(0), hullSkin: 0, ...defaultColours() }
        },
        records: { bestScore: 0, bestRank: 0, longestLifeMs: 0, totalKills: 0, totalSinks: 0, gamesPlayed: 0 },
        flags: { hasSeenControls: false }
    };
}

/**
 * A UUID v4. `crypto.randomUUID` where there is one, and a hand-rolled fallback where there is
 * not — it is only available on a secure context, and the game is served over plain HTTP in
 * development.
 *
 * It is **not a secret and not an authenticator**: anyone can craft one, nothing is granted on the
 * strength of it, and clearing storage makes a new player. See design/AccountData.md.
 */
export function newPlayerId(): string {
    const cryptoApi = globalThis.crypto;
    if (cryptoApi && typeof cryptoApi.randomUUID === "function") return cryptoApi.randomUUID();

    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = Math.floor(Math.random() * 16);
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
}

/** A name as it is stored: trimmed and truncated. Never rejected, only shortened. */
export function sanitiseName(name: string): string {
    return typeof name === "string" ? name.trim().slice(0, NAME_MAX_LENGTH) : "";
}

/**
 * Anything at all, as a usable `StoredPlayerData` — the same habit `coerceBoatTree` has, and for
 * the same reason: a hand-edited or half-written blob must degrade to something playable rather
 * than putting an `undefined` where a wallet goes.
 *
 * **A version from the future is read rather than discarded.** A player who has been on a newer
 * build, or who has had a rollback shipped to them, keeps every field this build understands; the
 * ones it does not are carried through untouched on the next write, so a downgrade does not
 * destroy data. That is why `stored` is spread over the result rather than replaced by it.
 */
export function coercePlayer(raw: unknown): StoredPlayerData {
    if (typeof raw !== "object" || raw === null) return freshPlayer();

    const stored = raw as Partial<StoredPlayerData> & Record<string, unknown>;
    const fresh = freshPlayer();
    const settings = (stored.settings ?? {}) as Partial<StoredPlayerData["settings"]>;
    const records = (stored.records ?? {}) as Partial<PlayerRecords>;

    return {
        ...stored,
        schemaVersion: SCHEMA_VERSION,
        playerId: typeof stored.playerId === "string" && stored.playerId.length > 0 ? stored.playerId : fresh.playerId,
        displayName: sanitiseName(stored.displayName ?? ""),
        lastPlayedAt: whole(stored.lastPlayedAt, fresh.lastPlayedAt),
        settings: {
            musicVolume: unit(settings.musicVolume, fresh.settings.musicVolume),
            sfxVolume: unit(settings.sfxVolume, fresh.settings.sfxVolume),
            muted: settings.muted === true,
            quality: settings.quality === "low" ? "low" : "high"
        },
        wallet: { doubloons: whole((stored.wallet ?? {}).doubloons, 0) },
        locker: coerceLocker(stored.locker),
        records: {
            bestScore: whole(records.bestScore, 0),
            bestRank: whole(records.bestRank, 0),
            longestLifeMs: whole(records.longestLifeMs, 0),
            totalKills: whole(records.totalKills, 0),
            totalSinks: whole(records.totalSinks, 0),
            gamesPlayed: whole(records.gamesPlayed, 0)
        },
        flags: { hasSeenControls: ((stored.flags ?? {}) as Record<string, unknown>).hasSeenControls === true }
    };
}

/**
 * The locker, with every index clamped into the catalogue as it stands **today**.
 *
 * The free boat and the free gun are always owned, whatever the file says: they cost nothing, a
 * captain who somehow lost them would have nothing to sail, and a save file is not the place to
 * discover that. Everything else is kept only if it names something that exists — a catalogue that
 * lost a boat leaves its owner with one fewer, which is the honest outcome and is visible in the
 * shop rather than being a crash somewhere else.
 */
function coerceLocker(raw: unknown): Locker {
    const stored = (raw ?? {}) as Partial<Locker>;
    return {
        presets: owned(stored.presets, BOAT.presets.length),
        cannons: owned(stored.cannons, BOAT.cannons.length),
        equipped: coerceEquipped(stored.equipped)
    };
}

/**
 * One equipped boat, clamped into the catalogue as it stands **today**: the hull and both guns
 * through `sanitiseLoadout`, the colours through the tree's own rules.
 *
 * Exported because the write path uses it as well as the read path. A picker that offered a colour
 * the tree does not allow must not be able to leave it in the file, and the way to guarantee that
 * is for saving and loading to run the same function rather than two that agree today.
 */
export function coerceEquipped(raw: unknown): EquippedBoat {
    const equipped = (raw ?? {}) as Partial<EquippedBoat>;
    const shipped = defaultColours();
    const loadout = loadoutOf({
        preset: whole(equipped.preset, 0),
        cannons: Array.isArray(equipped.cannons) ? equipped.cannons.map((type) => whole(type, 0)) : [],
        hullSkin: 0,
        ...shipped
    });

    return {
        preset: loadout.preset,
        cannons: [...loadout.cannons],
        hullSkin: whole(equipped.hullSkin, 0),
        // A colour the file does not have, or has as something that is not a colour, is the
        // catalogue's default — which is also what a version-2 save gets, since its `hullColour`
        // and `sailColour` were indices and are simply not read.
        sailTint: colour(equipped.sailTint, shipped.sailTint),
        hullTint: colour(equipped.hullTint, shipped.hullTint),
        emblemTint: colour(equipped.emblemTint, shipped.emblemTint),
        emblem: emblemIndex(equipped.emblem)
    };
}

/** What the catalogue says a captain who has never chosen puts to sea in. */
function defaultColours(): Pick<EquippedBoat, "sailTint" | "hullTint" | "emblemTint" | "emblem"> {
    const { defaults } = BOAT.cosmetics;
    return { sailTint: defaults.sailTint, hullTint: defaults.hullTint, emblemTint: defaults.emblemTint, emblem: defaults.emblem };
}

/** A stored `"#rrggbb"`, lower-cased. Anything else is the default — the same rule the tree uses. */
function colour(raw: unknown, fallback: string): string {
    if (typeof raw !== "string") return fallback;
    const value = raw.trim().toLowerCase();
    return /^#[0-9a-f]{6}$/.test(value) ? value : fallback;
}

/**
 * A stored device that this build still has. Out of range is `None` rather than the nearest, for
 * the reason `resolveFitout` gives: flying somebody else's device is a worse answer than flying
 * none.
 */
function emblemIndex(raw: unknown): number {
    const index = Math.floor(Number(raw));
    if (!Number.isFinite(index) || index < 0 || index >= BOAT.cosmetics.emblems.length) return BOAT.cosmetics.defaults.emblem;
    return index;
}

/** An owned-index list: whole, in range, unique, sorted, and always carrying the free entry. */
function owned(raw: unknown, count: number): number[] {
    const kept = new Set<number>([0]);
    if (Array.isArray(raw)) {
        for (const entry of raw) {
            const index = Math.floor(Number(entry));
            if (Number.isFinite(index) && index >= 0 && index < count) kept.add(index);
        }
    }

    return [...kept].sort((a, b) => a - b);
}

/** A finite, non-negative whole number, or the fallback. */
function whole(raw: unknown, fallback: number): number {
    const value = Math.floor(Number(raw));
    return Number.isFinite(value) && value >= 0 ? value : fallback;
}

/** A finite number in `[0, 1]`, or the fallback. */
function unit(raw: unknown, fallback: number): number {
    const value = Number(raw);
    return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : fallback;
}
